import { createContext, useContext, useState, ReactNode } from 'react'

type BaseTableData = {
  id: string
  label: string
  value: string
}

type GroupedTableData = {
  id: string
  label: string
  value: string
}

type NetworkData = {
  id: string
  name: string
  interactions: Array<{
    id: string
    label: string
    value: string
  }>
}

type CenaRedeData = {
  interactionsWeight: string
  likesP95: string
  likesP95P99: string
  likesP99: string
  commentsP95: string
  commentsP95P99: string
  commentsP99: string
  approachMultiplier: string
  channelMultiplier: string
  complementaryMultiplier: string
  keyMessage: string
  filterLikesFB: string
  filterCommentsFB: string
  filterSharesFB: string
  filterLikesYT: string
  filterCommentsYT: string
  filterLikesTT: string
  filterCommentsTT: string
  filterSharesTT: string
  extraPointP95: string
  networkApproachData: BaseTableData[]
  networkVehicleData: BaseTableData[]
  ultraPerformanceData: NetworkData[]
}

type CenaImprensaData = {
  formatWeight: string
  approachMultiplier: string
  vehicleMultiplier: string
  complementaryMultiplier: string
  keyMessage: string
  spokespersonMentioned: string
  titleMention: string
  pressApproachData: BaseTableData[]
  pressVehicleData: BaseTableData[]
  mediaTypeData: GroupedTableData[]
}

type FavorabilityData = {
  positive: string
  favorable: string
  unfavorable: string
  negative: string
}

type CenaContextData = {
  rede: CenaRedeData
  imprensa: CenaImprensaData
  favorabilidade: FavorabilityData
  updateRede: (data: Partial<CenaRedeData>) => void
  updateImprensa: (data: Partial<CenaImprensaData>) => void
  updateFavorabilidade: (data: Partial<FavorabilityData>) => void
  getAllData: () => { rede: CenaRedeData; imprensa: CenaImprensaData; favorabilidade: FavorabilityData }
}

const CenaContext = createContext<CenaContextData | undefined>(undefined)

export function CenaProvider({ children }: { children: ReactNode }) {
  const [rede, setRede] = useState<CenaRedeData>({
    interactionsWeight: '',
    likesP95: '',
    likesP95P99: '',
    likesP99: '',
    commentsP95: '',
    commentsP95P99: '',
    commentsP99: '',
    approachMultiplier: '',
    channelMultiplier: '',
    complementaryMultiplier: '',
    keyMessage: '',
    filterLikesFB: '',
    filterCommentsFB: '',
    filterSharesFB: '',
    filterLikesYT: '',
    filterCommentsYT: '',
    filterLikesTT: '',
    filterCommentsTT: '',
    filterSharesTT: '',
    extraPointP95: '',
    networkApproachData: [
      { id: 'protagonista', label: 'Protagonista', value: '' },
      { id: 'coadjuvante', label: 'Coadjuvante', value: '' },
      { id: 'citacao', label: 'Citação', value: '' },
    ],
    networkVehicleData: [
      { id: 'instagram', label: 'Instagram', value: '' },
      { id: 'facebook', label: 'Facebook', value: '' },
      { id: 'x', label: 'X', value: '' },
      { id: 'youtube', label: 'YouTube', value: '' },
    ],
    ultraPerformanceData: [
      {
        id: 'facebook',
        name: 'Facebook',
        interactions: [
          { id: 'curtida', label: 'Curtida', value: '' },
          { id: 'comentario', label: 'Comentário', value: '' },
          { id: 'compartilhamento', label: 'Compartilhamento', value: '' },
        ],
      },
      {
        id: 'x',
        name: 'X',
        interactions: [
          { id: 'curtida', label: 'Curtida', value: '' },
          { id: 'comentario', label: 'Comentário', value: '' },
          { id: 'compartilhamento', label: 'Compartilhamento', value: '' },
        ],
      },
      {
        id: 'instagram',
        name: 'Instagram',
        interactions: [
          { id: 'curtida', label: 'Curtida', value: '' },
          { id: 'comentario', label: 'Comentário', value: '' },
        ],
      },
      {
        id: 'youtube',
        name: 'YouTube',
        interactions: [
          { id: 'curtida', label: 'Curtida', value: '' },
          { id: 'comentario', label: 'Comentário', value: '' },
        ],
      },
      {
        id: 'tiktok',
        name: 'TikTok',
        interactions: [
          { id: 'curtida', label: 'Curtida', value: '' },
          { id: 'comentario', label: 'Comentário', value: '' },
          { id: 'compartilhamento', label: 'Compartilhamento', value: '' },
          { id: 'salvo', label: 'Salvo', value: '' },
        ],
      },
    ],
  })

  const [imprensa, setImprensa] = useState<CenaImprensaData>({
    formatWeight: '',
    approachMultiplier: '',
    vehicleMultiplier: '',
    complementaryMultiplier: '',
    keyMessage: '',
    spokespersonMentioned: '',
    titleMention: '',
    pressApproachData: [
      { id: 'protagonista', label: 'Protagonista', value: '' },
      { id: 'coadjuvante', label: 'Coadjuvante', value: '' },
      { id: 'citacao', label: 'Citação', value: '' },
    ],
    pressVehicleData: [
      { id: 'supertier', label: 'Super Tier', value: '' },
      { id: 'tier1', label: 'Tier 1', value: '' },
      { id: 'tier2', label: 'Tier 2', value: '' },
      { id: 'tier3', label: 'Tier 3', value: '' },
      { id: 'tier4', label: 'Tier 4', value: '' },
      { id: 'tier5', label: 'Tier 5', value: '' },
    ],
    mediaTypeData: [
      { id: 'Impressos e Online', label: 'P', value: '' },
      { id: 'Impressos e Online', label: 'M', value: '' },
      { id: 'Impressos e Online', label: 'G', value: '' },
      { id: 'TV', label: 'P', value: '' },
      { id: 'TV', label: 'M', value: '' },
      { id: 'TV', label: 'G', value: '' },
      { id: 'Rádio', label: 'P', value: '' },
      { id: 'Rádio', label: 'M', value: '' },
      { id: 'Rádio', label: 'G', value: '' },
    ],
  })

  const [favorabilidade, setFavorabilidade] = useState<FavorabilityData>({
    positive: '',
    favorable: '',
    unfavorable: '',
    negative: '',
  })

  const updateRede = (data: Partial<CenaRedeData>) => {
    setRede((prev) => ({ ...prev, ...data }))
  }

  const updateImprensa = (data: Partial<CenaImprensaData>) => {
    setImprensa((prev) => ({ ...prev, ...data }))
  }

  const updateFavorabilidade = (data: Partial<FavorabilityData>) => {
    setFavorabilidade((prev) => ({ ...prev, ...data }))
  }

  const getAllData = () => ({ rede, imprensa, favorabilidade })

  return (
    <CenaContext.Provider
      value={{
        rede,
        imprensa,
        favorabilidade,
        updateRede,
        updateImprensa,
        updateFavorabilidade,
        getAllData,
      }}>
      {children}
    </CenaContext.Provider>
  )
}

export function useCenaContext() {
  const context = useContext(CenaContext)
  if (!context) {
    throw new Error('useCenaContext deve ser usado dentro de CenaProvider')
  }
  return context
}
